import { useState, useEffect } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { api } from '@baaligames/shared-ui/lib/api';

function BetCard({ bet }: { bet: any }) {
  const isBack = bet.type === 'back';
  
  // Calculations
  let liability = bet.liability;
  let profit = 0;
  
  if (bet.marketName === 'Bookmaker' || bet.marketName === 'Session') {
    // For Fancy/Bookmaker
    if (isBack) {
      profit = bet.stake * (bet.odds / 100);
    } else {
      profit = bet.stake;
    }
  } else {
    // For Match Odds
    if (isBack) {
      profit = bet.stake * (bet.odds - 1);
    } else {
      profit = bet.stake;
    }
  }

  const riskFmt = Math.round(liability).toLocaleString('en-IN');
  const winFmt = Math.round(profit).toLocaleString('en-IN');
  const placedAt = new Date(bet.placedAt);
  const isSettled = bet.status === 'won' || bet.status === 'lost' || bet.status === 'rejected' || bet.status === 'refunded';

  return (
    <div className={`bg-white dark:bg-arena-800 rounded-2xl p-4 border overflow-hidden relative shadow-sm ${isBack ? 'border-blue-200 dark:border-blue-500/20' : 'border-pink-200 dark:border-pink-500/20'}`}>
      
      {/* Background tint matching bet type */}
      <div className={`absolute inset-0 opacity-[0.03] ${isBack ? 'bg-blue-500' : 'bg-pink-500'}`} style={{ pointerEvents: 'none' }} />
      
      <div className="relative z-10">
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-arena-400 mb-0.5">
              {bet.matchTitle} • {bet.marketName}
            </div>
            <div className="font-black text-lg text-arena-900 dark:text-white">
              {bet.selection}
            </div>
          </div>
          {isSettled ? (
            <div className={`px-2 py-1 rounded text-[10px] font-black tracking-widest ${
              bet.status === 'won' 
                ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400'
                : bet.status === 'lost' 
                ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400'
                : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-500/20 dark:text-neutral-400'
            }`}>
              {bet.status.toUpperCase()}
            </div>
          ) : (
            <div className={`px-2 py-1 rounded text-[10px] font-black tracking-widest ${
              isBack ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400' : 'bg-pink-100 text-pink-700 dark:bg-pink-500/20 dark:text-pink-400'
            }`}>
              {bet.status === 'pending' ? 'PENDING' : (isBack ? 'BACK' : 'LAY')}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mb-4 bg-arena-50 dark:bg-arena-900/50 p-2.5 rounded-xl border border-arena-100 dark:border-white/5">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-arena-400 uppercase tracking-widest">Odds</span>
            <span className="font-black text-arena-900 dark:text-white">{bet.odds}</span>
          </div>
          <div className="w-px h-6 bg-arena-200 dark:bg-white/10" />
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-arena-400 uppercase tracking-widest">Stake</span>
            <span className="font-black text-arena-900 dark:text-white">₹{bet.stake.toLocaleString('en-IN')}</span>
          </div>
        </div>

        {isSettled ? (
          <div className="flex justify-between items-center border-t border-arena-100 dark:border-white/5 pt-3 mt-1">
            <span className="text-xs font-bold text-arena-500 dark:text-arena-400">Return</span>
            <span className={`font-black text-lg ${bet.settlement_amount > 0 ? 'text-green-500' : 'text-neutral-500'}`}>
              {bet.settlement_amount > 0 ? '+' : ''}₹{Math.abs(bet.settlement_amount || 0).toLocaleString('en-IN')}
            </span>
          </div>
        ) : (
          <div className="flex justify-between items-center border-t border-arena-100 dark:border-white/5 pt-3 mt-1">
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-arena-400 uppercase tracking-widest">Risk</span>
              <span className="font-black text-red-500">₹{riskFmt}</span>
            </div>
            <div className="flex flex-col items-end">
              <span className="text-[10px] font-bold text-arena-400 uppercase tracking-widest">To Win</span>
              <span className="font-black text-green-500">₹{winFmt}</span>
            </div>
          </div>
        )}
        
        <div className="text-[9px] font-bold text-arena-400 mt-3 flex items-center gap-1">
          <Icon name="schedule" size={12} />
          {placedAt.toLocaleDateString()} {placedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          {' • '}
          ID: {bet.id.slice(0, 8)}
        </div>
      </div>
    </div>
  );
}

export default function MyBets() {
  const [tab, setTab] = useState<'open' | 'settled'>('open');
  const [allBets, setAllBets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await api.get<{ bets: any[] }>('/cricket/my-bets');
        setAllBets(res.bets || []);
      } catch (err) {
        console.error('Failed to load bets:', err);
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, []);

  const openBets = allBets.filter(b => b.status === 'open' || b.status === 'pending');
  const settledBets = allBets.filter(b => b.status === 'won' || b.status === 'lost' || b.status === 'rejected' || b.status === 'refunded');

  const bets = tab === 'open' ? openBets : settledBets;

  if (loading) {
    return <div className="text-center py-10 text-arena-400 font-bold animate-pulse">Loading bets...</div>;
  }

  return (
    <div className="space-y-4 max-w-3xl mx-auto w-full">
      {/* Tabs */}
      <div className="flex p-1 bg-arena-100 dark:bg-arena-800 rounded-xl mb-4">
        <button
          onClick={() => setTab('open')}
          className={`flex-1 py-2 text-sm font-black uppercase tracking-wider rounded-lg transition-all ${
            tab === 'open' 
              ? 'bg-white dark:bg-arena-700 text-arena-900 dark:text-white shadow-sm' 
              : 'text-arena-500 dark:text-arena-400 hover:text-arena-700 dark:hover:text-arena-300'
          }`}
        >
          Open Bets ({openBets.length})
        </button>
        <button
          onClick={() => setTab('settled')}
          className={`flex-1 py-2 text-sm font-black uppercase tracking-wider rounded-lg transition-all ${
            tab === 'settled' 
              ? 'bg-white dark:bg-arena-700 text-arena-900 dark:text-white shadow-sm' 
              : 'text-arena-500 dark:text-arena-400 hover:text-arena-700 dark:hover:text-arena-300'
          }`}
        >
          Settled ({settledBets.length})
        </button>
      </div>

      {/* List */}
      <div className="space-y-3">
        {bets.length === 0 ? (
          <div className="text-center py-10 text-arena-400 font-bold">
            No {tab} bets found.
          </div>
        ) : (
          bets.map(bet => <BetCard key={bet.id} bet={bet} />)
        )}
      </div>
    </div>
  );
}
